// Importazione delle dipendenze necessarie da Angular e altre librerie
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  ActivatedRoute, // Gestisce le informazioni sulla route attiva
  NavigationEnd, // Evento che si attiva quando una navigazione è completata
  Event as NavigationEvent, // Rappresenta un evento di navigazione
  NavigationStart,
  Router,
  RoutesRecognized,
} from '@angular/router';
import { environment } from '@env/environment'; // Per l'accesso alle variabili d'ambiente
import { Store } from '@ngrx/store'; // Gestione dello stato globale tramite NgRx
import { TranslateService } from '@ngx-translate/core'; // Per la traduzione del contenuto
import { LocalStorageService } from 'ngx-webstorage'; // Gestione della memorizzazione locale
import { filter, Observable, Subscription } from 'rxjs'; // RXJS per la gestione degli stream
import { LivechatService } from './livechat/livechat.service'; // Servizio per la chat live
import { isLogged } from './store/selectors/session.selector'; // Selettore per verificare se l'utente è loggato
import { GlobalState } from './store/store'; // Stato globale dell'applicazione
import { I18nService } from './translations/i18n/i18n.service'; // Servizio di internazionalizzazione

// Definizione del componente AppComponent
@Component({
  selector: 'app-root', // Selettore del componente
  templateUrl: './app.component.html', // Template HTML associato
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'new-my-sign'; // Titolo dell'applicazione

  logged$: Observable<boolean>; // Variabile osservabile per sapere se l'utente è loggato
  currentRoute: string; // Variabile per memorizzare la rotta corrente
  isClose = false; // Variabile per gestire la visibilità del tooltip
  event$: Subscription; // Variabile per la gestione degli eventi di navigazione
  closeTooltipSubscription: Subscription; // Variabile per l'abbonamento al servizio di chiusura del tooltip

  // Getter per verificare se la rotta corrente è la homepage
  get isHomepage(): boolean {
    return this.currentRoute === '/homepage';
  }

  // Getter per verificare se la rotta corrente è una blacklist
  get isBlacklistUrl(): boolean {
    return [
      '/login',
      '/oauth2',
      '/test',
      '/activation-steps',
      '/activation-steps/1',
      '/activation-steps/2',
      '/error',
    ].includes(this.currentRoute);
  }

  hideHeader = false; // Variabile per nascondere l'header
  hideFooter = false; // Variabile per nascondere il footer

  // Costruttore del componente
  constructor(
    private store: Store<GlobalState>, // Inizializza la gestione dello stato globale
    private i18nService: I18nService, // Servizio di traduzione
    private router: Router, // Router per la navigazione
    private translateService: TranslateService, // Servizio per la traduzione delle stringhe
    private activatedRoute: ActivatedRoute, // Per ottenere informazioni sulla route attiva
    private livechatService: LivechatService, // Servizio per la chat live
    private localStorageService: LocalStorageService // Servizio per gestire il localStorage
  ) {
    //Metodo A
    // Se nell'URL c'è un parametro 'identity', lo salvo nel localStorage redirect
    if (location.hash.includes('identity')) {
      console.log('identity');
      localStorage.setItem('identity', 'redirect');
    }
    // Gestione degli eventi di navigazione
    this.event$ = this.router.events.subscribe((event: NavigationEvent) => {
      // Quando la navigazione è completata (NavigationEnd)
      if (event instanceof NavigationEnd) {
        console.log(event);
        this.currentRoute = event.url; // Memorizza la rotta corrente

        // Se sono sulla homepage e c'è un 'identity' nel localStorage, rimuovilo e vai alla pagina di rinnovo
        if (
          this.currentRoute === '/homepage' &&
          localStorage.getItem('identity')
        ) {
          localStorage.removeItem('identity');
          this.router.navigate(['/renew']);
        }
      }
      //Metodo B alternativo
      // Quando una navigazione sta per iniziare (NavigationStart)
      if (event instanceof NavigationStart) {
        // Prendi il parametro 'identity' dai parametri della query
        this.activatedRoute.queryParams.subscribe(params => {
          const identity = params['identity'];
          if (identity) {
            console.log('Identity parameter found:', identity);
            // Se esiste, salva 'identity' nel localStorage
            localStorage.setItem('identity', String(identity));
          } else {
            console.log('Identity parameter not found.');
          }
        });
      }
      // Se l'evento è un RoutesRecognized, puoi aggiungere eventuali logiche
      if (event instanceof RoutesRecognized) {
      }
    });

    // Controlla se bisogna nascondere l'header o il footer dopo ogni navigazione
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.checkIfShouldHideHeaderAndFooter();
        this.updatePageTitle(); // Aggiorna il titolo della pagina
      });
  }

  // Funzione per controllare se bisogna nascondere l'header e il footer
  private checkIfShouldHideHeaderAndFooter() {
    // Prendi la route corrente
    const currentRoute = this.activatedRoute.firstChild;

    // Se la rotta è /login o /error, nascondi l'header e/o il footer
    this.hideHeader =
      currentRoute?.snapshot.routeConfig?.path === 'login' ||
      currentRoute?.snapshot.routeConfig?.path === 'error';
    this.hideFooter = currentRoute?.snapshot.routeConfig?.path === 'login';
  }

  // ngOnInit viene chiamato quando il componente è inizializzato
  ngOnInit(): void {
    // Inizializza l'osservabile per verificare se l'utente è loggato
    this.logged$ = this.store.select(isLogged);

    // Imposta la lingua del browser come lingua di default
    this.i18nService.init(
      navigator.language.replace('-', '_'),
      environment.supportedLanguages
    );

    // Abbonati al servizio di chiusura del tooltip, se la chat è aperta
    this.closeTooltipSubscription =
      this.livechatService.closeTooltip$.subscribe(() => {
        this.isClose = true;
      });

    // Auto-close tooltip dopo 30 secondi (compatibilità con tooltip precedente)
    setTimeout(() => {
      this.isClose = true;
    }, 30000);
  }

  // ngOnDestroy viene chiamato quando il componente è distrutto
  ngOnDestroy() {
    // Annulla l'abbonamento agli eventi del router
    this.event$.unsubscribe();
  }

  // Funzione per ottenere il titolo della pagina, tradotto
  get pageTitle(): string {
    const titleKey = this.router.config[0].children.find(route =>
      this.currentRoute.includes(route.path)
    ).data['breadcrumb'];
    return this.translateService.instant(titleKey);
  }

  // Funzione per aggiornare il titolo della pagina
  private updatePageTitle(): void {
    let title = 'MySign';

    if (this.currentRoute.includes('/login')) {
      title = 'Accedi a MySign - MySign';
    } else if (this.currentRoute.includes('/home')) {
      title = 'Home - MySign';
    } else if (this.currentRoute.includes('/certificate')) {
      title = 'Certificato di firma - MySign';
    } else if (this.currentRoute.includes('/otp')) {
      title = 'Codici OTP - MySign';
    } else if (this.currentRoute.includes('/pin')) {
      title = 'PIN di firma - MySign';
    } else if (this.currentRoute.includes('/renew')) {
      title = 'Rinnova firma digitale - MySign';
    } else if (this.currentRoute.includes('/suspend')) {
      title = 'Sospensione e revoca firma - MySign';
    } else if (this.currentRoute.includes('/report')) {
      title = 'Cronologia firme - MySign';
    }

    document.title = title;
  }
}
